/**
 * Real-world airframe geometry per ICAO type designator, used both to draw
 * each type's own top-down silhouette and to size it to scale on the map.
 *
 * Dimensions are published length / wingspan / fuselage width in metres
 * (rounded); the shape parameters (sweep, wing and tail placement, engine
 * layout) come from a family template with per-type overrides. Types not in
 * the table fall back to a family by ADS-B emitter category.
 */

export type Family =
  | 'narrow' // twin under-wing narrowbody (A320, 737, 757, A220)
  | 'wide' // twin under-wing widebody (777, 787, A330, A350, 767)
  | 'quad' // four under-wing engines (747, A380, A340)
  | 'trijet' // two under-wing + one in the tail (MD-11, DC-10)
  | 'aftTwin' // rear fuselage-mounted twin, T-tail (CRJ, ERJ, MD-80, 717, most bizjets)
  | 'aftTri' // three rear engines (Falcon 7X/8X/900)
  | 'overWing' // engines on pylons above the wing (HondaJet)
  | 'airlifter' // high-wing four-engine jet transport, T-tail (C-17)
  | 'turboprop' // twin wing-mounted props, straight wing (ATR, Dash 8, King Air)
  | 'quadProp' // four wing-mounted props (C-130, A400M)
  | 'single' // single-engine piston/turboprop GA (C172, PC-12)
  | 'topJet' // single jet engine on top of the rear fuselage (Cirrus Vision Jet)
  | 'fighter' // fast jet
  | 'heli'
  | 'glider'
  | 'balloon'
  | 'uav'
  | 'ground';

export interface EngineSpec {
  /** Spanwise position as a fraction of the semi-span (wing engines) — ignored for aft/tail mounts. */
  at: number;
  /** Nacelle diameter (m). */
  dia: number;
  /** Nacelle length (m). */
  len: number;
}

export interface Airframe {
  key: string; // icon cache key
  name: string;
  family: Family;
  length: number; // m
  span: number; // m
  fuselage: number; // fuselage width (m)
  /** Wing leading-edge sweep (deg). */
  sweep: number;
  /** Wing root leading edge, fraction of length from the nose. */
  wingAt: number;
  rootChord: number; // fraction of length
  tipChord: number; // fraction of length
  tTail: boolean;
  /** Horizontal tail span as a fraction of wingspan. */
  tailSpan: number;
  engines: EngineSpec[];
  /** Propeller diameter (m) for prop families. */
  prop?: number;
}

type Template = Omit<Airframe, 'key' | 'name' | 'length' | 'span' | 'fuselage' | 'engines'> & { engineAt: number[] };

const TEMPLATES: Record<Family, Template> = {
  narrow: { family: 'narrow', sweep: 27, wingAt: 0.36, rootChord: 0.17, tipChord: 0.045, tTail: false, tailSpan: 0.35, engineAt: [0.33] },
  wide: { family: 'wide', sweep: 32, wingAt: 0.37, rootChord: 0.19, tipChord: 0.045, tTail: false, tailSpan: 0.33, engineAt: [0.31] },
  quad: { family: 'quad', sweep: 37, wingAt: 0.36, rootChord: 0.2, tipChord: 0.045, tTail: false, tailSpan: 0.33, engineAt: [0.31, 0.58] },
  trijet: { family: 'trijet', sweep: 35, wingAt: 0.37, rootChord: 0.19, tipChord: 0.045, tTail: false, tailSpan: 0.36, engineAt: [0.33] },
  aftTwin: { family: 'aftTwin', sweep: 27, wingAt: 0.42, rootChord: 0.16, tipChord: 0.05, tTail: true, tailSpan: 0.35, engineAt: [] },
  aftTri: { family: 'aftTri', sweep: 30, wingAt: 0.4, rootChord: 0.17, tipChord: 0.05, tTail: false, tailSpan: 0.36, engineAt: [] },
  overWing: { family: 'overWing', sweep: 6, wingAt: 0.42, rootChord: 0.13, tipChord: 0.06, tTail: false, tailSpan: 0.36, engineAt: [0.3] },
  airlifter: { family: 'airlifter', sweep: 27, wingAt: 0.38, rootChord: 0.2, tipChord: 0.06, tTail: true, tailSpan: 0.38, engineAt: [0.3, 0.52] },
  turboprop: { family: 'turboprop', sweep: 3, wingAt: 0.38, rootChord: 0.09, tipChord: 0.05, tTail: true, tailSpan: 0.28, engineAt: [0.3] },
  quadProp: { family: 'quadProp', sweep: 3, wingAt: 0.36, rootChord: 0.13, tipChord: 0.07, tTail: false, tailSpan: 0.36, engineAt: [0.27, 0.53] },
  single: { family: 'single', sweep: 0, wingAt: 0.3, rootChord: 0.17, tipChord: 0.13, tTail: false, tailSpan: 0.32, engineAt: [] },
  topJet: { family: 'topJet', sweep: 4, wingAt: 0.34, rootChord: 0.16, tipChord: 0.09, tTail: false, tailSpan: 0.36, engineAt: [] },
  fighter: { family: 'fighter', sweep: 40, wingAt: 0.42, rootChord: 0.4, tipChord: 0.06, tTail: false, tailSpan: 0.6, engineAt: [] },
  heli: { family: 'heli', sweep: 0, wingAt: 0, rootChord: 0, tipChord: 0, tTail: false, tailSpan: 0, engineAt: [] },
  glider: { family: 'glider', sweep: 0, wingAt: 0, rootChord: 0, tipChord: 0, tTail: false, tailSpan: 0, engineAt: [] },
  balloon: { family: 'balloon', sweep: 0, wingAt: 0, rootChord: 0, tipChord: 0, tTail: false, tailSpan: 0, engineAt: [] },
  uav: { family: 'uav', sweep: 0, wingAt: 0, rootChord: 0, tipChord: 0, tTail: false, tailSpan: 0, engineAt: [] },
  ground: { family: 'ground', sweep: 0, wingAt: 0, rootChord: 0, tipChord: 0, tTail: false, tailSpan: 0, engineAt: [] },
};

/**
 * [family, length, span, fuselage width, engine nacelle diameter, name, overrides]
 * Engine nacelle length defaults to 1.9× its diameter (turbofans) — see build().
 */
type Row = [Family, number, number, number, number, string, Partial<Template>?];

const TYPES: Record<string, Row> = {
  // Airbus narrowbodies
  A318: ['narrow', 31.4, 34.1, 3.95, 2.0, 'A318'],
  A319: ['narrow', 33.8, 35.8, 3.95, 2.0, 'A319'],
  A320: ['narrow', 37.6, 35.8, 3.95, 2.0, 'A320'],
  A321: ['narrow', 44.5, 35.8, 3.95, 2.0, 'A321'],
  A19N: ['narrow', 33.8, 35.8, 3.95, 2.4, 'A319neo'],
  A20N: ['narrow', 37.6, 35.8, 3.95, 2.4, 'A320neo'],
  A21N: ['narrow', 44.5, 35.8, 3.95, 2.4, 'A321neo'],
  BCS1: ['narrow', 35.0, 35.1, 3.7, 2.1, 'A220-100', { sweep: 25, wingAt: 0.37 }],
  BCS3: ['narrow', 38.7, 35.1, 3.7, 2.1, 'A220-300', { sweep: 25, wingAt: 0.38 }],
  // Airbus widebodies
  A306: ['wide', 54.1, 44.8, 5.64, 2.7, 'A300-600', { sweep: 28 }],
  A310: ['wide', 46.7, 43.9, 5.64, 2.7, 'A310', { sweep: 28 }],
  A332: ['wide', 58.8, 60.3, 5.64, 2.9, 'A330-200', { sweep: 30 }],
  A333: ['wide', 63.7, 60.3, 5.64, 2.9, 'A330-300', { sweep: 30 }],
  A338: ['wide', 58.8, 64.0, 5.64, 3.4, 'A330-800neo', { sweep: 30 }],
  A339: ['wide', 63.7, 64.0, 5.64, 3.4, 'A330-900neo', { sweep: 30 }],
  A359: ['wide', 66.8, 64.75, 5.96, 3.6, 'A350-900', { sweep: 33 }],
  A35K: ['wide', 73.8, 64.75, 5.96, 3.7, 'A350-1000', { sweep: 33 }],
  A342: ['quad', 59.4, 60.3, 5.64, 2.2, 'A340-200', { sweep: 30 }],
  A343: ['quad', 63.7, 60.3, 5.64, 2.2, 'A340-300', { sweep: 30 }],
  A345: ['quad', 67.9, 63.5, 5.64, 2.7, 'A340-500', { sweep: 31 }],
  A346: ['quad', 75.4, 63.5, 5.64, 2.7, 'A340-600', { sweep: 31 }],
  A388: ['quad', 72.7, 79.8, 7.14, 3.5, 'A380-800', { sweep: 34, rootChord: 0.24, tailSpan: 0.38, engineAt: [0.29, 0.56] }],
  A400: ['quadProp', 45.1, 42.4, 5.64, 1.4, 'A400M', { sweep: 15, prop: 5.3, tTail: true }],
  // Boeing narrowbodies
  B712: ['aftTwin', 37.8, 28.4, 3.34, 1.9, '717-200', { sweep: 24, wingAt: 0.4 }],
  B733: ['narrow', 33.4, 28.9, 3.76, 1.9, '737-300'],
  B734: ['narrow', 36.4, 28.9, 3.76, 1.9, '737-400'],
  B735: ['narrow', 31.0, 28.9, 3.76, 1.9, '737-500'],
  B736: ['narrow', 31.2, 34.3, 3.76, 1.9, '737-600'],
  B737: ['narrow', 33.6, 35.8, 3.76, 1.9, '737-700'],
  B738: ['narrow', 39.5, 35.8, 3.76, 1.9, '737-800'],
  B739: ['narrow', 42.1, 35.8, 3.76, 1.9, '737-900'],
  B37M: ['narrow', 35.6, 35.9, 3.76, 2.2, '737 MAX 7'],
  B38M: ['narrow', 39.5, 35.9, 3.76, 2.2, '737 MAX 8'],
  B39M: ['narrow', 42.2, 35.9, 3.76, 2.2, '737 MAX 9'],
  B3XM: ['narrow', 43.8, 35.9, 3.76, 2.2, '737 MAX 10'],
  B752: ['narrow', 47.3, 38.1, 3.76, 2.5, '757-200', { sweep: 26, wingAt: 0.35 }],
  B753: ['narrow', 54.4, 38.1, 3.76, 2.5, '757-300', { sweep: 26, wingAt: 0.36 }],
  // Boeing widebodies
  B762: ['wide', 48.5, 47.6, 5.03, 2.8, '767-200', { sweep: 31.5 }],
  B763: ['wide', 54.9, 47.6, 5.03, 2.8, '767-300', { sweep: 31.5 }],
  B764: ['wide', 61.4, 51.9, 5.03, 2.8, '767-400', { sweep: 31.5 }],
  B772: ['wide', 63.7, 60.9, 6.2, 3.6, '777-200'],
  B77L: ['wide', 63.7, 64.8, 6.2, 3.9, '777-200LR'],
  B773: ['wide', 73.9, 60.9, 6.2, 3.6, '777-300'],
  B77W: ['wide', 73.9, 64.8, 6.2, 3.9, '777-300ER'],
  B778: ['wide', 70.9, 71.8, 6.2, 4.4, '777-8', { sweep: 33 }],
  B779: ['wide', 76.7, 71.8, 6.2, 4.4, '777-9', { sweep: 33 }],
  B788: ['wide', 56.7, 60.1, 5.77, 3.3, '787-8', { sweep: 34 }],
  B789: ['wide', 62.8, 60.1, 5.77, 3.3, '787-9', { sweep: 34 }],
  B78X: ['wide', 68.3, 60.1, 5.77, 3.3, '787-10', { sweep: 34 }],
  B742: ['quad', 70.6, 59.6, 6.5, 2.6, '747-200', { sweep: 40 }],
  B744: ['quad', 70.7, 64.4, 6.5, 2.7, '747-400', { sweep: 40 }],
  B748: ['quad', 76.3, 68.4, 6.5, 3.1, '747-8', { sweep: 40 }],
  // Douglas / McDonnell Douglas
  MD11: ['trijet', 61.2, 51.7, 6.02, 2.9, 'MD-11'],
  DC10: ['trijet', 55.5, 50.4, 6.02, 2.9, 'DC-10'],
  MD81: ['aftTwin', 45.1, 32.9, 3.34, 1.8, 'MD-81', { sweep: 24, wingAt: 0.42 }],
  MD82: ['aftTwin', 45.1, 32.9, 3.34, 1.8, 'MD-82', { sweep: 24, wingAt: 0.42 }],
  MD83: ['aftTwin', 45.1, 32.9, 3.34, 1.8, 'MD-83', { sweep: 24, wingAt: 0.42 }],
  MD87: ['aftTwin', 39.8, 32.9, 3.34, 1.8, 'MD-87', { sweep: 24, wingAt: 0.4 }],
  MD88: ['aftTwin', 45.1, 32.9, 3.34, 1.8, 'MD-88', { sweep: 24, wingAt: 0.42 }],
  MD90: ['aftTwin', 46.5, 32.9, 3.34, 2.1, 'MD-90', { sweep: 24, wingAt: 0.42 }],
  // Embraer
  E135: ['aftTwin', 26.3, 20.0, 2.28, 1.3, 'ERJ-135', { sweep: 23 }],
  E145: ['aftTwin', 29.9, 20.0, 2.28, 1.3, 'ERJ-145', { sweep: 23 }],
  E170: ['narrow', 29.9, 26.0, 3.0, 1.6, 'E170', { sweep: 25, wingAt: 0.37 }],
  E75S: ['narrow', 31.7, 26.0, 3.0, 1.6, 'E175', { sweep: 25, wingAt: 0.37 }],
  E75L: ['narrow', 31.7, 28.6, 3.0, 1.6, 'E175', { sweep: 25, wingAt: 0.37 }],
  E190: ['narrow', 36.2, 28.7, 3.0, 1.8, 'E190', { sweep: 25 }],
  E195: ['narrow', 38.7, 28.7, 3.0, 1.8, 'E195', { sweep: 25 }],
  E290: ['narrow', 36.2, 33.7, 3.0, 2.1, 'E190-E2', { sweep: 25 }],
  E295: ['narrow', 41.5, 35.1, 3.0, 2.1, 'E195-E2', { sweep: 25 }],
  E50P: ['aftTwin', 12.8, 12.3, 1.6, 0.9, 'Phenom 100', { sweep: 12, tTail: true }],
  E55P: ['aftTwin', 15.9, 16.2, 1.7, 1.0, 'Phenom 300', { sweep: 20, tTail: true }],
  E545: ['aftTwin', 19.7, 20.0, 2.1, 1.2, 'Praetor 500 / Legacy 450', { sweep: 25 }],
  E35L: ['aftTwin', 26.3, 21.2, 2.28, 1.3, 'Legacy 600/650', { sweep: 23 }],
  // Bombardier regional + business
  CRJ1: ['aftTwin', 26.8, 21.2, 2.69, 1.4, 'CRJ100'],
  CRJ2: ['aftTwin', 26.8, 21.2, 2.69, 1.4, 'CRJ200'],
  CRJ7: ['aftTwin', 32.5, 23.2, 2.69, 1.5, 'CRJ700'],
  CRJ9: ['aftTwin', 36.2, 24.9, 2.69, 1.5, 'CRJ900'],
  CRJX: ['aftTwin', 39.1, 26.2, 2.69, 1.5, 'CRJ1000'],
  CL30: ['aftTwin', 20.9, 19.5, 2.18, 1.3, 'Challenger 300', { sweep: 27, tTail: true }],
  CL35: ['aftTwin', 20.9, 21.0, 2.18, 1.3, 'Challenger 350', { sweep: 27, tTail: true }],
  CL60: ['aftTwin', 20.9, 19.6, 2.69, 1.4, 'Challenger 600-series', { sweep: 25 }],
  GLEX: ['aftTwin', 30.3, 28.7, 2.69, 1.6, 'Global Express', { sweep: 35, tTail: true }],
  GL5T: ['aftTwin', 29.5, 28.7, 2.69, 1.6, 'Global 5000/5500', { sweep: 35, tTail: true }],
  GL7T: ['aftTwin', 33.8, 31.7, 2.69, 1.8, 'Global 7500', { sweep: 35, tTail: true }],
  // Gulfstream
  GLF4: ['aftTwin', 26.9, 23.7, 2.39, 1.4, 'Gulfstream IV', { sweep: 30, tTail: true }],
  GLF5: ['aftTwin', 29.4, 28.5, 2.39, 1.5, 'Gulfstream V / G550', { sweep: 30, tTail: true }],
  GLF6: ['aftTwin', 30.4, 30.4, 2.59, 1.6, 'Gulfstream G650', { sweep: 36, tTail: true }],
  GA6C: ['aftTwin', 31.4, 28.5, 2.59, 1.6, 'Gulfstream G600', { sweep: 36, tTail: true }],
  GA7C: ['aftTwin', 33.5, 31.4, 2.59, 1.7, 'Gulfstream G700', { sweep: 36, tTail: true }],
  G280: ['aftTwin', 20.3, 19.2, 2.18, 1.2, 'Gulfstream G280', { sweep: 30, tTail: false }],
  // Cessna / Textron jets
  C510: ['aftTwin', 12.4, 13.2, 1.55, 0.9, 'Citation Mustang', { sweep: 0, tTail: false }],
  C525: ['aftTwin', 13.0, 14.3, 1.6, 0.9, 'CitationJet', { sweep: 0, tTail: false }],
  C25A: ['aftTwin', 14.5, 15.1, 1.6, 0.9, 'Citation CJ2', { sweep: 0, tTail: false }],
  C25B: ['aftTwin', 15.6, 16.3, 1.6, 0.9, 'Citation CJ3', { sweep: 0, tTail: false }],
  C25C: ['aftTwin', 16.3, 15.5, 1.6, 1.0, 'Citation CJ4', { sweep: 12, tTail: false }],
  C560: ['aftTwin', 14.9, 15.9, 1.7, 1.0, 'Citation V', { sweep: 0, tTail: false }],
  C56X: ['aftTwin', 15.8, 17.2, 1.7, 1.1, 'Citation Excel', { sweep: 0, tTail: false }],
  C68A: ['aftTwin', 19.0, 22.0, 1.96, 1.2, 'Citation Latitude', { sweep: 15, tTail: true }],
  C700: ['aftTwin', 22.3, 21.0, 1.96, 1.3, 'Citation Longitude', { sweep: 28, tTail: true }],
  C750: ['aftTwin', 22.0, 19.4, 1.7, 1.3, 'Citation X', { sweep: 37, tTail: true }],
  // Learjet, Dassault, Pilatus, Honda
  LJ35: ['aftTwin', 14.8, 12.0, 1.6, 0.9, 'Learjet 35', { sweep: 13, tTail: true }],
  LJ45: ['aftTwin', 17.7, 14.6, 1.6, 1.0, 'Learjet 45', { sweep: 13, tTail: true }],
  LJ60: ['aftTwin', 17.9, 13.4, 1.6, 1.0, 'Learjet 60', { sweep: 13, tTail: true }],
  LJ75: ['aftTwin', 17.7, 15.5, 1.6, 1.0, 'Learjet 75', { sweep: 13, tTail: true }],
  F2TH: ['aftTwin', 20.2, 19.3, 2.5, 1.3, 'Falcon 2000', { sweep: 28, tTail: false }],
  F900: ['aftTri', 20.2, 19.3, 2.5, 1.1, 'Falcon 900'],
  FA7X: ['aftTri', 23.2, 26.2, 2.5, 1.2, 'Falcon 7X'],
  FA8X: ['aftTri', 24.5, 26.3, 2.5, 1.2, 'Falcon 8X'],
  FA6X: ['aftTwin', 25.7, 25.9, 2.58, 1.5, 'Falcon 6X', { sweep: 33, tTail: false }],
  PC24: ['aftTwin', 16.9, 17.0, 1.7, 1.0, 'Pilatus PC-24', { sweep: 8, tTail: true }],
  HDJT: ['overWing', 13.0, 12.1, 1.5, 0.9, 'HondaJet'],
  // Military / special jets
  C17: ['airlifter', 53.0, 51.75, 6.85, 2.5, 'C-17 Globemaster III'],
  K35R: ['quad', 41.5, 39.9, 3.66, 2.1, 'KC-135R', { sweep: 35 }],
  E3TF: ['quad', 46.6, 44.4, 3.76, 1.9, 'E-3 Sentry', { sweep: 35 }],
  P8: ['narrow', 39.5, 37.6, 3.76, 1.9, 'P-8 Poseidon'],
  C5M: ['airlifter', 75.3, 67.9, 7.0, 3.0, 'C-5M Galaxy', { sweep: 25 }],
  F16: ['fighter', 15.0, 9.96, 1.4, 1.0, 'F-16'],
  F18S: ['fighter', 18.3, 13.6, 1.6, 1.0, 'F/A-18E/F'],
  F35: ['fighter', 15.7, 10.7, 1.6, 1.1, 'F-35'],
  // Turboprops
  AT43: ['turboprop', 22.7, 24.6, 2.87, 1.0, 'ATR 42-300', { prop: 3.9 }],
  AT45: ['turboprop', 22.7, 24.6, 2.87, 1.0, 'ATR 42-500', { prop: 3.9 }],
  AT46: ['turboprop', 22.7, 24.6, 2.87, 1.0, 'ATR 42-600', { prop: 3.9 }],
  AT72: ['turboprop', 27.2, 27.05, 2.87, 1.1, 'ATR 72', { prop: 3.9 }],
  AT75: ['turboprop', 27.2, 27.05, 2.87, 1.1, 'ATR 72-500', { prop: 3.9 }],
  AT76: ['turboprop', 27.2, 27.05, 2.87, 1.1, 'ATR 72-600', { prop: 3.9 }],
  DH8A: ['turboprop', 22.3, 25.9, 2.69, 1.1, 'Dash 8-100', { prop: 3.7 }],
  DH8C: ['turboprop', 25.7, 27.4, 2.69, 1.1, 'Dash 8-300', { prop: 4.0 }],
  DH8D: ['turboprop', 32.8, 28.4, 2.69, 1.3, 'Dash 8-400', { prop: 4.1, engineAt: [0.29] }],
  SF34: ['turboprop', 19.7, 21.4, 2.3, 0.9, 'Saab 340', { prop: 3.35, tTail: false }],
  B190: ['turboprop', 17.6, 17.7, 1.8, 0.8, '1900', { prop: 2.8, tTail: true }],
  BE20: ['turboprop', 13.3, 16.6, 1.4, 0.7, 'King Air 200', { prop: 2.5, tTail: true }],
  B350: ['turboprop', 14.2, 17.6, 1.4, 0.7, 'King Air 350', { prop: 2.7, tTail: true }],
  BE9L: ['turboprop', 10.8, 15.3, 1.4, 0.7, 'King Air 90', { prop: 2.4, tTail: false }],
  C130: ['quadProp', 29.8, 40.4, 4.3, 1.2, 'C-130 Hercules', { prop: 4.1 }],
  C30J: ['quadProp', 29.8, 40.4, 4.3, 1.2, 'C-130J', { prop: 4.1 }],
  // Single-engine
  C208: ['single', 11.5, 15.9, 1.6, 0, 'Cessna Caravan', { prop: 2.7, wingAt: 0.28 }],
  PC12: ['single', 14.4, 16.3, 1.6, 0, 'Pilatus PC-12', { prop: 2.7, tTail: true, wingAt: 0.36 }],
  TBM9: ['single', 10.7, 12.8, 1.4, 0, 'TBM 900-series', { prop: 2.3, wingAt: 0.35 }],
  TBM7: ['single', 10.6, 12.8, 1.4, 0, 'TBM 700', { prop: 2.3, wingAt: 0.35 }],
  C152: ['single', 7.3, 10.2, 1.0, 0, 'Cessna 152', { prop: 1.75 }],
  C172: ['single', 8.3, 11.0, 1.1, 0, 'Cessna 172', { prop: 1.9 }],
  C182: ['single', 8.8, 11.0, 1.1, 0, 'Cessna 182', { prop: 2.1 }],
  C206: ['single', 8.6, 11.0, 1.2, 0, 'Cessna 206', { prop: 2.1 }],
  P28A: ['single', 7.3, 10.7, 1.1, 0, 'Piper Cherokee', { prop: 1.9 }],
  PA46: ['single', 8.7, 13.1, 1.3, 0, 'Piper Malibu', { prop: 2.0 }],
  SR22: ['single', 7.9, 11.7, 1.3, 0, 'Cirrus SR22', { prop: 2.0 }],
  SR20: ['single', 7.9, 11.7, 1.3, 0, 'Cirrus SR20', { prop: 1.9 }],
  DA40: ['single', 8.0, 11.9, 1.1, 0, 'Diamond DA40', { prop: 1.8, tTail: true }],
  BE36: ['single', 8.4, 10.2, 1.2, 0, 'Bonanza A36', { prop: 2.0 }],
  // More business jets
  H25B: ['aftTwin', 15.6, 15.7, 1.8, 1.0, 'Hawker 800', { sweep: 20, tTail: false }],
  BE40: ['aftTwin', 14.8, 13.3, 1.6, 0.9, 'Beechjet 400', { sweep: 20, tTail: true }],
  PRM1: ['aftTwin', 14.0, 13.6, 1.7, 0.9, 'Premier I', { sweep: 20, tTail: false }],
  E550: ['aftTwin', 20.7, 20.3, 2.1, 1.2, 'Praetor 600 / Legacy 500', { sweep: 25 }],
  C501: ['aftTwin', 13.3, 14.4, 1.6, 0.9, 'Citation I SP', { sweep: 0, tTail: false }],
  C550: ['aftTwin', 14.4, 15.9, 1.6, 1.0, 'Citation II/Bravo', { sweep: 0, tTail: false }],
  C25M: ['aftTwin', 12.9, 14.4, 1.6, 0.9, 'Citation M2', { sweep: 0, tTail: false }],
  C650: ['aftTwin', 16.9, 16.3, 1.7, 1.0, 'Citation III/VII', { sweep: 25, tTail: true }],
  C680: ['aftTwin', 19.4, 19.3, 1.9, 1.2, 'Citation Sovereign', { sweep: 16, tTail: false }],
  G150: ['aftTwin', 17.3, 16.9, 1.8, 1.0, 'Gulfstream G150', { sweep: 30, tTail: true }],
  GA5C: ['aftTwin', 27.6, 26.3, 2.59, 1.5, 'Gulfstream G500', { sweep: 36, tTail: true }],
  FA50: ['aftTri', 18.5, 18.9, 2.3, 1.1, 'Falcon 50'],
  E45X: ['aftTwin', 29.9, 20.0, 2.28, 1.3, 'ERJ-145XR', { sweep: 23 }],
  SF50: ['topJet', 9.4, 11.8, 1.6, 0.9, 'Cirrus Vision Jet'],
  // Piston and turboprop twins
  BE30: ['turboprop', 14.2, 16.6, 1.4, 0.7, 'King Air 300', { prop: 2.7, tTail: true }],
  BE55: ['turboprop', 8.5, 11.5, 1.2, 0.7, 'Baron 55', { prop: 1.9, tTail: false }],
  BE58: ['turboprop', 9.1, 11.5, 1.2, 0.7, 'Baron 58', { prop: 1.9, tTail: false }],
  BE95: ['turboprop', 7.9, 11.5, 1.2, 0.6, 'Travel Air', { prop: 1.8, tTail: false }],
  C310: ['turboprop', 9.7, 11.3, 1.2, 0.7, 'Cessna 310', { prop: 1.9, tTail: false }],
  C340: ['turboprop', 10.5, 11.6, 1.3, 0.8, 'Cessna 340', { prop: 1.9, tTail: false }],
  C402: ['turboprop', 11.1, 13.5, 1.4, 0.8, 'Cessna 402', { prop: 2.0, tTail: false }],
  PA23: ['turboprop', 9.5, 11.3, 1.2, 0.7, 'Piper Aztec', { prop: 1.9, tTail: false }],
  PA31: ['turboprop', 9.9, 12.4, 1.3, 0.8, 'Piper Navajo', { prop: 2.0, tTail: false }],
  PA34: ['turboprop', 8.7, 11.9, 1.2, 0.7, 'Piper Seneca', { prop: 1.9, tTail: false }],
  PA44: ['turboprop', 8.4, 11.8, 1.1, 0.6, 'Piper Seminole', { prop: 1.9, tTail: true }],
  DA62: ['turboprop', 9.2, 14.6, 1.2, 0.7, 'Diamond DA62', { prop: 1.9, tTail: true }],
  MU2: ['turboprop', 12.0, 11.9, 1.4, 0.8, 'Mitsubishi MU-2', { prop: 2.5, tTail: false }],
  P180: ['turboprop', 14.4, 14.0, 1.8, 0.8, 'Piaggio Avanti', { prop: 2.2, tTail: true, wingAt: 0.55 }],
  // More single-engine
  EPIC: ['single', 10.8, 13.1, 1.4, 0, 'Epic E1000', { prop: 2.3, wingAt: 0.35 }],
  P46T: ['single', 8.8, 13.1, 1.3, 0, 'Piper Meridian', { prop: 2.1 }],
  TEX2: ['single', 10.2, 10.2, 1.1, 0, 'T-6 Texan II', { prop: 2.4, wingAt: 0.34 }],
  S22T: ['single', 7.9, 11.7, 1.3, 0, 'Cirrus SR22T', { prop: 1.9 }],
  M20P: ['single', 7.5, 11.0, 1.1, 0, 'Mooney M20', { prop: 1.9 }],
  C150: ['single', 7.3, 10.2, 1.0, 0, 'Cessna 150', { prop: 1.75 }],
  C170: ['single', 7.6, 11.0, 1.1, 0, 'Cessna 170', { prop: 1.9 }],
  C180: ['single', 7.8, 10.9, 1.1, 0, 'Cessna 180', { prop: 2.0 }],
  C185: ['single', 7.9, 10.9, 1.1, 0, 'Cessna 185', { prop: 2.1 }],
  C210: ['single', 8.6, 11.2, 1.2, 0, 'Cessna 210', { prop: 2.1 }],
  C72R: ['single', 8.3, 11.0, 1.1, 0, 'Cessna 172RG', { prop: 1.9 }],
  C82R: ['single', 8.8, 11.0, 1.1, 0, 'Cessna 182RG', { prop: 2.1 }],
  C82S: ['single', 8.8, 11.0, 1.1, 0, 'Cessna 182 Skylane', { prop: 2.1 }],
  C82T: ['single', 8.8, 11.0, 1.1, 0, 'Cessna T182', { prop: 2.1 }],
  T206: ['single', 8.6, 11.0, 1.2, 0, 'Cessna T206', { prop: 2.1 }],
  BE23: ['single', 7.8, 10.0, 1.1, 0, 'Beech Musketeer', { prop: 1.9 }],
  BE33: ['single', 7.7, 10.2, 1.2, 0, 'Bonanza 33', { prop: 2.0 }],
  BE35: ['single', 8.1, 10.2, 1.2, 0, 'Bonanza 35', { prop: 2.0 }],
  P28B: ['single', 7.3, 9.2, 1.1, 0, 'Piper Cherokee 235', { prop: 1.9 }],
  P28R: ['single', 7.6, 9.8, 1.1, 0, 'Piper Arrow', { prop: 1.9 }],
  PA24: ['single', 7.6, 11.0, 1.1, 0, 'Piper Comanche', { prop: 1.9 }],
  PA32: ['single', 8.4, 11.0, 1.2, 0, 'Piper Cherokee Six', { prop: 2.0 }],
  P32R: ['single', 8.5, 11.0, 1.2, 0, 'Piper Saratoga', { prop: 2.0 }],
  AA5: ['single', 6.7, 9.6, 1.0, 0, 'Grumman Tiger', { prop: 1.8 }],
  HUSK: ['single', 6.9, 10.8, 0.9, 0, 'Aviat Husky', { prop: 2.0 }],
  RV6: ['single', 6.1, 7.0, 1.0, 0, "Van's RV-6", { prop: 1.7 }],
  RV7: ['single', 6.2, 7.6, 1.0, 0, "Van's RV-7", { prop: 1.7 }],
  RV8: ['single', 6.4, 7.3, 0.9, 0, "Van's RV-8", { prop: 1.7 }],
  RV10: ['single', 7.4, 9.6, 1.2, 0, "Van's RV-10", { prop: 1.9 }],
  RV12: ['single', 6.0, 8.1, 1.0, 0, "Van's RV-12", { prop: 1.7 }],
  // Helicopters (length incl. rotor, rotor diameter as span)
  A119: ['heli', 13.0, 10.8, 1.4, 0, 'AW119'],
  AS65: ['heli', 13.7, 11.9, 1.8, 0, 'AS365 Dauphin'],
  EC35: ['heli', 12.2, 10.2, 1.5, 0, 'H135'],
  EC45: ['heli', 13.0, 11.0, 1.6, 0, 'H145'],
  AS50: ['heli', 12.9, 10.7, 1.5, 0, 'AS350'],
  R44: ['heli', 11.7, 10.1, 1.3, 0, 'Robinson R44'],
  R22: ['heli', 8.8, 7.7, 1.1, 0, 'Robinson R22'],
  B06: ['heli', 11.9, 10.2, 1.3, 0, 'Bell 206'],
  B407: ['heli', 12.7, 10.7, 1.5, 0, 'Bell 407'],
  S76: ['heli', 16.0, 13.4, 2.1, 0, 'S-76'],
  H60: ['heli', 19.8, 16.4, 2.4, 0, 'UH-60 Black Hawk'],
  A139: ['heli', 16.7, 13.8, 2.0, 0, 'AW139'],
};

function build(key: string, row: Row): Airframe {
  const [family, length, span, fuselage, engineDia, name, over] = row;
  const t = { ...TEMPLATES[family], ...over };
  return {
    key,
    name,
    family,
    length,
    span,
    fuselage,
    sweep: t.sweep,
    wingAt: t.wingAt,
    rootChord: t.rootChord,
    tipChord: t.tipChord,
    tTail: t.tTail,
    tailSpan: t.tailSpan,
    prop: t.prop,
    engines: t.engineAt.map((at) => ({ at, dia: engineDia, len: engineDia * (family === 'turboprop' || family === 'quadProp' ? 2.6 : 1.9) })),
  };
}

const CACHE = new Map<string, Airframe>();

/** Generic airframes for aircraft whose type designator is unknown, by ADS-B emitter category. */
const FALLBACK_BY_CATEGORY: Record<string, Row> = {
  A1: ['single', 8.3, 11.0, 1.1, 0, 'Light aircraft', { prop: 1.9 }],
  A2: ['turboprop', 14.2, 17.6, 1.4, 0.7, 'Small aircraft', { prop: 2.7 }],
  A3: ['narrow', 37.6, 35.8, 3.9, 2.0, 'Large aircraft'],
  A4: ['narrow', 47.3, 38.1, 3.76, 2.5, 'High-vortex large'],
  A5: ['wide', 70, 64, 6.0, 3.6, 'Heavy'],
  A6: ['fighter', 16, 11, 1.5, 1.0, 'High performance'],
  A7: ['heli', 12.9, 10.7, 1.5, 0, 'Rotorcraft'],
  B1: ['glider', 7, 18, 0.7, 0, 'Glider'],
  B2: ['balloon', 20, 18, 1, 0, 'Balloon'],
  B4: ['single', 6, 9, 0.9, 0, 'Ultralight', { prop: 1.6 }],
  B6: ['uav', 2, 2, 0.4, 0, 'UAV'],
  B7: ['fighter', 16, 11, 1.5, 1.0, 'Spacecraft'],
  C1: ['ground', 7, 2.5, 2.5, 0, 'Surface vehicle'],
  C2: ['ground', 7, 2.5, 2.5, 0, 'Surface vehicle'],
  C3: ['ground', 7, 2.5, 2.5, 0, 'Surface vehicle'],
};

/** The airframe to draw for an aircraft: its exact type when known, else a category stand-in. */
export function airframeFor(typeCode: string | null, category: string | null): Airframe {
  const code = typeCode?.trim().toUpperCase() || '';
  const key = code && TYPES[code] ? code : `_${category && FALLBACK_BY_CATEGORY[category] ? category : 'A3'}`;
  let a = CACHE.get(key);
  if (!a) {
    a = build(key, key.startsWith('_') ? FALLBACK_BY_CATEGORY[key.slice(1)] : TYPES[key]);
    CACHE.set(key, a);
  }
  return a;
}

/** Airframe by its icon cache key (as produced by airframeFor). */
export function airframeByKey(key: string): Airframe | null {
  if (CACHE.has(key)) return CACHE.get(key)!;
  if (key.startsWith('_')) {
    const row = FALLBACK_BY_CATEGORY[key.slice(1)];
    return row ? airframeFor(null, key.slice(1)) : null;
  }
  return TYPES[key] ? airframeFor(key, null) : null;
}

// ---------------------------------------------------------------- make/model

/** Manufacturer by type designator: exact overrides first, then prefix rules (first match wins). */
const MAKER_EXACT: Record<string, string> = {
  BCS1: 'Airbus',
  BCS3: 'Airbus',
  A400: 'Airbus',
  C17: 'Boeing',
  K35R: 'Boeing',
  E3TF: 'Boeing',
  P8: 'Boeing',
  F18S: 'Boeing',
  C5M: 'Lockheed',
  C130: 'Lockheed',
  C30J: 'Lockheed Martin',
  F16: 'General Dynamics',
  F35: 'Lockheed Martin',
  HDJT: 'Honda',
  H25B: 'Hawker',
  BE40: 'Beechcraft',
  PRM1: 'Beechcraft',
  TEX2: 'Beechcraft',
  SF50: 'Cirrus',
  S22T: 'Cirrus',
  M20P: 'Mooney',
  MU2: 'Mitsubishi',
  P180: 'Piaggio',
  EPIC: 'Epic',
  AA5: 'Grumman',
  HUSK: 'Aviat',
  A119: 'Leonardo',
  A139: 'Leonardo',
  S76: 'Sikorsky',
  H60: 'Sikorsky',
  B06: 'Bell',
  B407: 'Bell',
  F2TH: 'Dassault',
  F900: 'Dassault',
};

const MAKER_PREFIX: [RegExp, string][] = [
  [/^A\d/, 'Airbus'],
  [/^(B7|B3|B4)/, 'Boeing'],
  [/^(MD|DC)/, 'McDonnell Douglas'],
  [/^E\d/, 'Embraer'],
  [/^(CRJ|CL|GL[57E])/, 'Bombardier'],
  [/^(GLF|GA\d|G[12]\d0)/, 'Gulfstream'],
  [/^LJ/, 'Learjet'],
  [/^FA/, 'Dassault'],
  [/^PC/, 'Pilatus'],
  [/^AT/, 'ATR'],
  [/^DH8/, 'De Havilland Canada'],
  [/^SF34/, 'Saab'],
  [/^(B190|BE|B350)/, 'Beechcraft'],
  [/^(C\d|T206)/, 'Cessna'],
  [/^(P28|PA|P32|P46)/, 'Piper'],
  [/^SR/, 'Cirrus'],
  [/^DA/, 'Diamond'],
  [/^RV/, "Van's Aircraft"],
  [/^TBM/, 'Daher'],
  [/^(EC|AS)/, 'Airbus Helicopters'],
  [/^R\d/, 'Robinson'],
];

function makerOf(code: string): string | null {
  if (MAKER_EXACT[code]) return MAKER_EXACT[code];
  for (const [re, maker] of MAKER_PREFIX) if (re.test(code)) return maker;
  return null;
}

const FAMILY_DESC: Record<Family, string> = {
  narrow: 'Narrow-body twin jet',
  wide: 'Wide-body twin jet',
  quad: 'Four-engine jet',
  trijet: 'Three-engine wide-body',
  aftTwin: 'Twin jet, rear-mounted engines',
  aftTri: 'Three-engine business jet',
  overWing: 'Twin jet, over-wing engines',
  airlifter: 'Four-engine military airlifter',
  turboprop: 'Twin-engine propeller aircraft',
  quadProp: 'Four-engine turboprop',
  single: 'Single-engine propeller aircraft',
  topJet: 'Single-engine personal jet',
  fighter: 'Fast jet',
  heli: 'Helicopter',
  glider: 'Glider',
  balloon: 'Balloon',
  uav: 'Drone',
  ground: 'Ground vehicle',
};

function engineCount(a: Airframe): number | null {
  switch (a.family) {
    case 'aftTwin':
      return 2;
    case 'aftTri':
      return 3;
    case 'trijet':
      return a.engines.length * 2 + 1; // a pair on the wings plus one in the tail
    case 'topJet':
    case 'single':
      return 1;
    case 'heli':
    case 'glider':
    case 'balloon':
    case 'uav':
    case 'ground':
    case 'fighter':
      return null;
    default:
      return a.engines.length * 2;
  }
}

export interface AircraftModel {
  /** ICAO type designator, e.g. "A388". */
  code: string;
  maker: string | null;
  /** Full make and model, e.g. "Airbus A380-800". */
  label: string;
  /** e.g. "Four-engine jet". */
  kind: string;
  engines: number | null;
  lengthM: number;
  spanM: number;
  /** Icon/airframe key, for drawing its silhouette. */
  key: string;
}

/** Make/model details for a known type designator, or null if it isn't in the table. */
export function aircraftModel(typeCode: string | null): AircraftModel | null {
  const code = typeCode?.trim().toUpperCase();
  if (!code || !TYPES[code]) return null;
  const a = airframeFor(code, null);
  const maker = makerOf(code);
  const label = maker && !a.name.toLowerCase().startsWith(maker.toLowerCase().split(' ')[0]) ? `${maker} ${a.name}` : a.name;
  return {
    code,
    maker,
    label,
    kind: FAMILY_DESC[a.family],
    engines: engineCount(a),
    lengthM: a.length,
    spanM: a.span,
    key: a.key,
  };
}
